import { createServerFn } from "@tanstack/react-start";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { rowToBlogPost } from "@/lib/blog-cms";
import { renderBlogMarkdown } from "@/lib/markdown-render.server";
import { BLOG_CATEGORIES, type BlogCategory, type BlogPost } from "@/lib/blog";

// Columnas ligeras para listado (sin content_markdown ni faqs para eficiencia)
const LIST_COLUMNS =
  "slug,title,h1,excerpt,category,primary_keyword,secondary_keywords,intent,service_links,related_posts,cta_label,cta_href,published_at,updated_at,image,read_minutes";

// Validadores manuales en vez de zod a propósito en las 3 fns de este
// archivo: los validators viajan también al bundle del cliente y estas fns
// se llaman desde los loaders de las rutas públicas del blog — un schema de
// zod acá arrastraba zod (y lib/blog-cms entero) al JS de esas páginas.
// Mismo criterio que getSeoOverrideForPath (seo-override.fns.ts).
export const getPublishedPostBySlug = createServerFn({ method: "POST" })
  .validator((input: unknown): { slug: string } => {
    const slug = (input as { slug?: unknown } | null)?.slug;
    if (typeof slug !== "string") throw new Error("slug inválido");
    return { slug };
  })
  .handler(async ({ data }) => {
    const { data: row, error } = await supabaseAdmin
      .from("blog_posts")
      .select("*")
      .eq("slug", data.slug)
      .eq("status", "publicado")
      .single();

    if (error || !row) return null;
    const post = rowToBlogPost(row as Record<string, unknown>);
    return { ...post, contentHtml: renderBlogMarkdown(post.contentMarkdown) };
  });

export const getPublishedPostsByCategory = createServerFn({ method: "POST" })
  .validator((input: unknown): { category: BlogCategory } => {
    const category = (input as { category?: unknown } | null)?.category;
    if (typeof category !== "string" || !BLOG_CATEGORIES.includes(category as BlogCategory)) {
      throw new Error("category inválida");
    }
    return { category: category as BlogCategory };
  })
  .handler(async ({ data }) => {
    const { data: rows, error } = await supabaseAdmin
      .from("blog_posts")
      .select(LIST_COLUMNS)
      .eq("status", "publicado")
      .eq("category", data.category)
      .order("published_at", { ascending: false })
      // Desempate estable: varios posts comparten fecha (los cargados al crear el CMS).
      .order("created_at", { ascending: false })
      .order("slug");

    if (error) throw new Error(error.message);
    return ((rows ?? []) as Record<string, unknown>[]).map((row) =>
      rowToBlogPost({ ...row, content_markdown: "", faqs: [] }),
    ) as BlogPost[];
  });

export const getPublishedPostsBySlugList = createServerFn({ method: "POST" })
  .validator((input: unknown): { slugs: string[] } => {
    const slugs = (input as { slugs?: unknown } | null)?.slugs;
    if (!Array.isArray(slugs) || slugs.length > 6 || slugs.some((s) => typeof s !== "string")) {
      throw new Error("slugs inválidos");
    }
    return { slugs: slugs as string[] };
  })
  .handler(async ({ data }) => {
    if (!data.slugs.length) return [] as BlogPost[];
    const { data: rows, error } = await supabaseAdmin
      .from("blog_posts")
      .select(LIST_COLUMNS)
      .eq("status", "publicado")
      .in("slug", data.slugs);

    if (error) throw new Error(error.message);
    return ((rows ?? []) as Record<string, unknown>[]).map((row) =>
      rowToBlogPost({ ...row, content_markdown: "", faqs: [] }),
    ) as BlogPost[];
  });
